import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Project, TeamMember, TimelineItem, DataContextType } from '../types';
import { useLanguage } from './LanguageContext';
import { useLocation } from 'react-router-dom';

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within a DataProvider');
  return context;
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    const validRoutes = ['/', '/admin'];
    const isErrorPage = !validRoutes.includes(location.pathname);

    if (isErrorPage) {
      setLoading(false);
      return;
    }

    if (hasFetched) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [projectsRes, teamRes, timelineRes] = await Promise.all([
          fetch('/api/projects'),
          fetch('/api/team'),
          fetch('/api/timeline'),
        ]);
        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (teamRes.ok) setTeam(await teamRes.json());
        if (timelineRes.ok) setTimeline(await timelineRes.json());
        setHasFetched(true);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.pathname, hasFetched]);

  const addProject = async (p: Omit<Project, 'id'>) => {
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(p),
      });
      if (res.ok) {
        const newProject = await res.json();
        setProjects([newProject, ...projects]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const addTeamMember = async (t: Omit<TeamMember, 'id'>) => {
    try {
      const res = await fetch('/api/team', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(t),
      });
      if (res.ok) {
        const newMember = await res.json();
        setTeam([newMember, ...team]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const addTimelineItem = async (t: Omit<TimelineItem, 'id'>) => {
    try {
      const res = await fetch('/api/timeline', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(t),
      });
      if (res.ok) {
        const newItem = await res.json();
        setTimeline([newItem, ...timeline].sort((a, b) => b.year.localeCompare(a.year)));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const updateItem = async (type: 'project' | 'team' | 'timeline', item: any) => {
    try {
      const res = await fetch(`/api/${type === 'project' ? 'projects' : type}/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      });
      if (res.ok) {
        const updated = await res.json();
        if (type === 'project') {
          setProjects(projects.map((p) => (p.id === updated.id ? updated : p)));
        }
        if (type === 'team') {
          setTeam(team.map((t) => (t.id === updated.id ? updated : t)));
        }
        if (type === 'timeline') {
          setTimeline(timeline.map((t) => (t.id === updated.id ? updated : t)));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteItem = async (type: 'project' | 'team' | 'timeline', id: string) => {
    try {
      const res = await fetch(`/api/${type === 'project' ? 'projects' : type}/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        if (type === 'project') setProjects(projects.filter((p) => p.id !== id));
        if (type === 'team') setTeam(team.filter((t) => t.id !== id));
        if (type === 'timeline') setTimeline(timeline.filter((t) => t.id !== id));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const reorderItems = async (type: 'project' | 'team' | 'timeline', items: any[]) => {
    if (type === 'project') setProjects(items);
    if (type === 'team') setTeam(items);
    if (type === 'timeline') setTimeline(items);
    try {
      await fetch(`/api/${type === 'project' ? 'projects' : type}/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(items),
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#050c15]">
        <div className="relative flex flex-col items-center">
          <div className="relative w-24 h-24">
            <div className="absolute inset-0 rounded-full border-t-2 border-rs-bright animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-r-2 border-rs-dark animate-spin-reverse"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-rs-bright rounded-full animate-ping"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DataContext.Provider
      value={{
        projects,
        team,
        timeline,
        addProject,
        addTeamMember,
        addTimelineItem,
        updateItem,
        deleteItem,
        reorderItems,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
